import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const heroBadgeRef = useRef(null);
  const heroH1Ref    = useRef(null);
  const heroParaRef  = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
  ]);

  const projects = [
    { id: 1, title: 'E-Commerce Platform', category: 'Web Development', summary: 'Modern e-commerce platform with seamless checkout and inventory management.', description: 'A full-featured e-commerce solution built with React, Node.js, and MongoDB. Features include product management, shopping cart functionality, secure payment processing, order tracking, and an admin dashboard for inventory management.', imageUrl: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'AWS'], link: 'https://example.com/ecommerce-project', client: 'RetailMaster Inc.', duration: '4 months', completed: 'January 2023' },
    { id: 2, title: 'Fitness Tracking App', category: 'Mobile App', summary: 'Cross-platform mobile app for fitness tracking and personalized workout plans.', description: 'A comprehensive fitness tracking application built with React Native that works on both iOS and Android. Features include workout tracking, progress visualization, personalized workout recommendations, and social sharing capabilities.', imageUrl: 'https://images.unsplash.com/photo-1543286386-2e659306cd6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', technologies: ['React Native', 'Firebase', 'Redux', 'Apple HealthKit', 'Google Fit'], link: 'https://example.com/fitness-app', client: 'FitLife Solutions', duration: '6 months', completed: 'March 2023' },
    { id: 3, title: 'Corporate Brand Redesign', category: 'UI/UX Design', summary: 'Complete brand overhaul and digital presence redesign for a finance company.', description: "A comprehensive brand redesign project that included a new visual identity, website redesign, and digital marketing materials. The project focused on creating a modern, trustworthy brand image that resonates with the client's target audience.", imageUrl: 'https://images.unsplash.com/photo-1613909207039-6b173b755cc1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80', technologies: ['Adobe Creative Suite', 'Figma', 'WordPress', 'SCSS'], link: 'https://example.com/brand-redesign', client: 'FinTrust Capital', duration: '3 months', completed: 'November 2022' },
    { id: 4, title: 'Cloud Migration Solution', category: 'Cloud Services', summary: 'Enterprise-level cloud migration and infrastructure modernization.', description: 'A large-scale cloud migration project that involved moving a legacy on-premise infrastructure to AWS. The project included architecture redesign, data migration, and implementation of DevOps practices for continuous integration and deployment.', imageUrl: 'https://images.unsplash.com/photo-1535191042502-e6a9a3d407e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins'], link: 'https://example.com/cloud-migration', client: 'GlobalTech Industries', duration: '8 months', completed: 'May 2023' },
    { id: 5, title: 'SEO & Content Strategy', category: 'Digital Marketing', summary: 'Comprehensive SEO overhaul resulting in 200% organic traffic increase.', description: 'A strategic SEO and content marketing project that included technical SEO improvements, content strategy development, and implementation of an ongoing content calendar. The project resulted in a significant increase in organic traffic and lead generation.', imageUrl: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1174&q=80', technologies: ['Google Analytics', 'SEMrush', 'Ahrefs', 'WordPress', 'HubSpot'], link: 'https://example.com/seo-strategy', client: 'GrowthMarketing Co.', duration: '6 months (ongoing)', completed: 'Current' },
    { id: 6, title: 'Blockchain Payment System', category: 'Blockchain', summary: 'Secure, decentralized payment processing system using blockchain technology.', description: 'A blockchain-based payment processing system that provides secure, transparent transactions with reduced fees. The system includes wallet functionality, smart contracts for automated payments, and an admin interface for transaction monitoring.', imageUrl: 'https://images.unsplash.com/photo-1639322537228-f710d846310a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1632&q=80', technologies: ['Ethereum', 'Solidity', 'Web3.js', 'React', 'Node.js'], link: 'https://example.com/blockchain-payment', client: 'PayChain Systems', duration: '5 months', completed: 'February 2023' },
    { id: 7, title: 'AI-Powered Customer Service', category: 'AI Integration', summary: 'Intelligent chatbot and customer service automation for a telecommunications company.', description: 'An AI-powered customer service solution that includes a chatbot with natural language processing capabilities, automated ticket routing, and an analytics dashboard. The system has significantly reduced response times and improved customer satisfaction.', imageUrl: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80', technologies: ['TensorFlow', 'N8N', 'Natural Language Processing', 'React', 'Node.js'], link: 'https://example.com/ai-customer-service', client: 'TeleCom Group', duration: '7 months', completed: 'April 2023' },
    { id: 8, title: 'Healthcare Management Platform', category: 'Web Development', summary: 'Comprehensive healthcare management system for patient care and administrative tasks.', description: 'A web-based healthcare management platform that streamlines patient care and administrative processes. Features include appointment scheduling, patient records management, billing integration, and reporting tools for healthcare providers.', imageUrl: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80', technologies: ['Angular', 'Node.js', 'PostgreSQL', 'HIPAA Compliance', 'Docker'], link: 'https://example.com/healthcare-platform', client: 'MedCare Solutions', duration: '10 months', completed: 'December 2022' },
  ];

  const categories = ['all', 'Web Development', 'Mobile App', 'UI/UX Design', 'Cloud Services', 'Digital Marketing', 'Blockchain', 'AI Integration'];

  useEffect(() => {
    setFilteredProjects(filter === 'all' ? projects : projects.filter((p) => p.category === filter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  /* Gallery: alternating x-slide entrance per row */
  const galleryRef = useRef(null);
  useEffect(() => {
    if (!galleryRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.project-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, x: 0, y: 0 });
        return;
      }
      cards.forEach((c, i) => {
        gsap.set(c, {
          opacity: 0,
          x: i % 2 === 0 ? -40 : 40,
          y: 30,
          willChange: 'transform',
        });
        ScrollTrigger.create({
          trigger: c,
          start: 'top 88%',
          once: true,
          onEnter: () => {
            gsap.to(c, {
              opacity: 1, x: 0, y: 0,
              duration: 0.95, ease: 'power3.out',
            });
          },
        });
      });
    }, galleryRef);
    return () => ctx.revert();
  }, [filteredProjects]);

  const openModal = (project) => {
    setActiveProject(project);
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> Selected Works · Portfolio
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-6">
              Our <span className="gradient-text">Projects</span>
            </h1>
            <p ref={heroParaRef} className="narrative-lg">
              Explore our portfolio of successful projects that showcase our expertise,
              craftsmanship, and innovation across industries.
            </p>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <section className="py-10 bg-paper-2 border-y border-hairline">
        <div className="container-custom">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar justify-center flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`shrink-0 px-5 py-2.5 text-sm font-semibold rounded-full border transition-all duration-300 ${
                  filter === c
                    ? 'text-white border-transparent shadow-soft'
                    : 'bg-white text-ink-soft border-hairline hover:text-nexcess-blue hover:border-nexcess-blue/40'
                }`}
                style={
                  filter === c
                    ? { background: 'linear-gradient(135deg, #0984e3, #00b0ff, #d500f9)' }
                    : {}
                }
              >
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="section-padding bg-paper">
        <div className="container-custom">
          <div ref={galleryRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <motion.article
                key={project.id}
                layout
                exit={{ opacity: 0, scale: 0.92, filter: 'blur(4px)', transition: { duration: 0.35 } }}
                onClick={() => openModal(project)}
                className="project-card group cursor-pointer bg-white rounded-2xl border border-hairline shadow-soft hover:shadow-soft-lg transition-shadow duration-300 overflow-hidden flex flex-col"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                style={{ willChange: 'transform' }}
              >
                <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={project.imageUrl}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 left-4">
                    <span className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-nexcess-blue font-bold shadow-soft">
                      Nº {String(project.id).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-2">
                    {project.category}
                  </div>
                  <h3 className="heading-sm mb-2 leading-snug">{project.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed line-clamp-2 mb-5">{project.summary}</p>
                  <div className="mt-auto flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-nexcess-blue group-hover:gap-2.5 transition-all">
                      View Case Study <FaArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
            </AnimatePresence>
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="narrative-body">No projects match this filter yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* MODAL */}
      <AnimatePresence>
        {showModal && activeProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-ink/60 backdrop-blur-md flex items-center justify-center z-[60] p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-soft-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <div className="relative overflow-hidden rounded-t-3xl" style={{ aspectRatio: '21/9' }}>
                  <img src={activeProject.imageUrl} alt={activeProject.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent" />
                  <button
                    className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/90 backdrop-blur text-ink hover:bg-white shadow-soft flex items-center justify-center transition-all"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <FaTimes size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
                    <span className="inline-block text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full bg-white/15 backdrop-blur mb-3">
                      {activeProject.category}
                    </span>
                    <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
                      {activeProject.title}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="md:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Overview</div>
                  <p className="narrative-body mb-8">{activeProject.description}</p>

                  <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Tech Stack</div>
                  <div className="flex flex-wrap gap-2">
                    {activeProject.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1.5 rounded-full bg-paper-2 border border-hairline text-ink font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="md:border-l border-hairline md:pl-10 space-y-5">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-1">Client</div>
                    <p className="font-semibold text-ink">{activeProject.client}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-1">Duration</div>
                    <p className="font-semibold text-ink">{activeProject.duration}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-1">Completed</div>
                    <p className="font-semibold text-ink">{activeProject.completed}</p>
                  </div>
                  <a
                    href={activeProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary inline-flex w-full justify-center"
                  >
                    Visit Live <FaExternalLinkAlt size={11} />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-paper">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center text-white shadow-soft-lg" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5 text-white">
                Ready to Start Your Project?
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Let's turn your vision into reality with our expertise in technology and design.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-nexcess-blue font-semibold shadow-soft hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300"
              >
                Get in Touch <HiArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Projects;
